/**
 * Table Trigger Button
 * 
 * A button that opens a simple dropdown for creating tables
 * with row and column input fields (similar to link dialog).
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { Table as TableIcon } from 'lucide-react';

interface TableTriggerButtonProps {
    editor: Editor | null;
    maxRows?: number;
    maxCols?: number;
    hideWhenUnavailable?: boolean;
    text?: string;
    onInserted?: (rows: number, cols: number) => void;
}

export const useTableTriggerButton = ({
    editor,
    maxRows = 20,
    maxCols = 20,
    hideWhenUnavailable = false,
    onInserted,
}: Omit<TableTriggerButtonProps, 'text'>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputRows, setInputRows] = useState(3);
    const [inputCols, setInputCols] = useState(3);

    const canInsert = editor?.can().insertTable?.() ?? false;
    const isVisible = !hideWhenUnavailable || canInsert;

    const handleInsert = useCallback(() => {
        if (!editor || inputRows < 1 || inputCols < 1) return;

        editor
            .chain()
            .focus()
            .insertTable({ rows: inputRows, cols: inputCols, withHeaderRow: true })
            .run();

        setIsOpen(false);
        onInserted?.(inputRows, inputCols);
    }, [editor, inputRows, inputCols, onInserted]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleInsert();
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    }, [handleInsert]);

    return {
        isVisible,
        canInsert,
        isOpen,
        setIsOpen,
        inputRows,
        setInputRows,
        inputCols,
        setInputCols,
        handleInsert,
        handleKeyDown,
        maxRows,
        maxCols,
        label: 'Insert Table',
        Icon: TableIcon,
    };
};

export const TableTriggerButton: React.FC<TableTriggerButtonProps> = ({
    editor,
    maxRows = 20,
    maxCols = 20,
    hideWhenUnavailable = false,
    text,
    onInserted,
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const rowInputRef = useRef<HTMLInputElement>(null);
    const {
        isVisible,
        canInsert,
        isOpen,
        setIsOpen,
        inputRows,
        setInputRows,
        inputCols,
        setInputCols,
        handleInsert,
        handleKeyDown,
        label,
        Icon,
    } = useTableTriggerButton({
        editor,
        maxRows,
        maxCols,
        hideWhenUnavailable,
        onInserted,
    });

    // Focus row input when dropdown opens
    useEffect(() => {
        if (isOpen && rowInputRef.current) {
            rowInputRef.current.focus();
            rowInputRef.current.select();
        }
    }, [isOpen]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen, setIsOpen]);

    if (!isVisible) return null;

    return (
        <div className="table-trigger-button" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                disabled={!canInsert}
                title={label}
                aria-label={label}
                aria-expanded={isOpen}
                className={isOpen ? 'active' : ''}
            >
                <Icon size={16} />
                {text && <span>{text}</span>}
            </button>

            {isOpen && (
                <div className="table-insert-dropdown" onKeyDown={handleKeyDown}>
                    <div className="input-group">
                        <div className="input-field">
                            <label htmlFor="table-rows">Rows</label>
                            <input
                                ref={rowInputRef}
                                id="table-rows"
                                type="number"
                                min={1}
                                max={maxRows}
                                value={inputRows}
                                onChange={(e) => setInputRows(Math.min(Math.max(1, parseInt(e.target.value) || 1), maxRows))}
                            />
                        </div>
                        <span className="separator">×</span>
                        <div className="input-field">
                            <label htmlFor="table-cols">Cols</label>
                            <input
                                id="table-cols"
                                type="number"
                                min={1}
                                max={maxCols}
                                value={inputCols}
                                onChange={(e) => setInputCols(Math.min(Math.max(1, parseInt(e.target.value) || 1), maxCols))}
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        className="insert-button"
                        onClick={handleInsert}
                    >
                        Insert Table
                    </button>
                </div>
            )}
        </div>
    );
};

export default TableTriggerButton;
